import { hideToast, showTooltipToast } from "../Toasts";

/** Preserve the image separately while converting tooltip HTML to inert text. */
function tooltipContent(target: Element): {text: string; iconUrl?: string} {
  const content = target.getAttribute("data-tooltip-content"); // no itx
  if (content !== null) return { text: content.trim() };
  const html = target.getAttribute("data-tooltip-html"); // no itx
  if (!html) return { text: "" };
  const template = target.ownerDocument.createElement("template");
  template.innerHTML = html
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(?:p|div|tr|li|h[1-6])\s*>/gi, "\n")
    .replace(/<\/t[dh]\s*>/gi, "\t");
  const icon = template.content.querySelector("img"); // no itx
  return { text: template.content.textContent?.trim() ?? "", iconUrl: icon?.getAttribute("src") || undefined };
}

/** Presence of data-tv-toast opts an ordinary tooltip target into toast delivery. */
export function observeToastTooltips(root: Document = document): () => void {
  let active: Element | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let toastToken: number | undefined;
  const clear = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    active = undefined;
    if (toastToken !== undefined) void hideToast(toastToken);
    toastToken = undefined;
  };
  const enter = (event: Event) => {
    const target = event.target instanceof Element ? event.target.closest("[data-tv-toast]") : null;
    if (target === active) return;
    clear();
    if (!target || (event instanceof PointerEvent && event.buttons !== 0)) return;
    active = target;
    const delay = Number(target.getAttribute("data-tooltip-delay-show") ?? 500); // no itx
    timer = setTimeout(
      () => {
        timer = undefined;
        if (
          active !== target ||
          !target.isConnected ||
          !target.hasAttribute(
            "data-tv-toast", // no itx
          )
        )
          return;
        const { text, iconUrl } = tooltipContent(target);
        if (text)
          toastToken = showTooltipToast(text, iconUrl, {
            backgroundColor: target.getAttribute("data-tooltip-background-color") || undefined,
            color: target.getAttribute("data-tooltip-color") || undefined,
          });
      },
      Number.isFinite(delay) ? Math.max(0, delay) : 500,
    );
  };
  const leave = (event: Event) => {
    const next = (event as PointerEvent | FocusEvent).relatedTarget;
    if (next instanceof Node && active?.contains(next)) return;
    clear();
  };
  root.addEventListener("pointerover", enter);
  root.addEventListener("pointerout", leave);
  root.addEventListener("focusin", enter);
  root.addEventListener("focusout", leave);
  root.addEventListener("pointerdown", clear, true);
  return () => {
    clear();
    root.removeEventListener("pointerover", enter);
    root.removeEventListener("pointerout", leave);
    root.removeEventListener("focusin", enter);
    root.removeEventListener("focusout", leave);
    root.removeEventListener("pointerdown", clear, true);
  };
}
